/**
 * Dynamic Table Section Component
 *
 * Renders a configurable data table with columns, actions, and data fetching.
 * Supports loading states, empty states, and responsive design.
 */

import React from 'react';
import { TabSection, TableSectionConfig } from '../../../types/tabConfiguration';
import { useCSAudits, CSAudit } from '../../../services/queries/configurationQueries';
import { Table, Plus } from 'lucide-react';
import { StandardButton } from '../../standard';

interface DynamicTableSectionProps {
  /** Section configuration */
  section: TabSection;
  /** Optional record ID for filtering data */
  recordId?: string;
  /** Optional custom class name */
  className?: string;
  /** Callback when View Audit Details action is triggered */
  onViewAuditDetails?: (auditId: string) => void;
}

/**
 * Dynamic Table Section
 *
 * Renders a data table with configurable columns and actions
 */
const DynamicTableSection: React.FC<DynamicTableSectionProps> = ({
  section,
  recordId,
  className = '',
  onViewAuditDetails
}) => {
  // Cast config to TableSectionConfig
  const config = section.config as TableSectionConfig;

  // Call all hooks unconditionally at the top level
  // Add new query hooks here as they are created
  const csAuditsQuery = useCSAudits(recordId);

  // Determine which data to use based on data source configuration
  let data: any[] | undefined;
  let isLoading: boolean;
  let error: Error | null;

  // Check if dataSource exists
  if (!config || !config.dataSource) {
    throw new Error(`Missing dataSource configuration in table section: ${section.id}`);
  }

  if (config.dataSource.type === 'query' && config.dataSource.query) {
    const objectType = config.dataSource.query.objectType;
    switch (objectType) {
      case 'cs_audits':
        ({ data, isLoading, error } = csAuditsQuery);
        break;
      default:
        throw new Error(`Unknown query object type: ${objectType}`);
    }
  } else if (config.dataSource.type === 'static' && config.dataSource.data) {
    data = config.dataSource.data;
    isLoading = false;
    error = null;
  } else {
    throw new Error(`Unsupported data source type: ${config.dataSource?.type || 'undefined'}. Full dataSource: ${JSON.stringify(config.dataSource)}`);
  }

  /**
   * Loading State
   */
  if (isLoading) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${className}`}>
        <div className="flex items-start">
          <Table className="h-8 w-8 text-blue-500 mr-4 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {section.label}
            </h3>
            <div className="animate-pulse space-y-3">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Error State
   */
  if (error) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${className}`}>
        <div className="flex items-start">
          <Table className="h-8 w-8 text-red-500 mr-4 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {section.label}
            </h3>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-200 font-medium mb-2">
                Error Loading Table Data
              </p>
              <p className="text-sm text-red-600 dark:text-red-400">
                {error instanceof Error ? error.message : 'Failed to load table data'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Empty State
   */
  if (!data || data.length === 0) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${className}`}>
        <div className="flex items-start">
          <Table className="h-8 w-8 text-gray-400 mr-4 mt-1" />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {section.label}
              </h3>
              <StandardButton
                variant="primary"
                size="sm"
                leftIcon={<Plus className="h-4 w-4" />}
              >
                New
              </StandardButton>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700 rounded-lg p-12 text-center">
              <Table className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400 mb-1 font-medium">
                No records found
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                Get started by creating a new record
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Table with Data
   */
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow ${className}`}>
      {/* Section Header */}
      <div className="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-start">
            <Table className="h-6 w-6 text-blue-500 mr-3 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {section.label}
              </h3>
              {section.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {section.description}
                </p>
              )}
            </div>
          </div>
          {/* Optional "New" button */}
          <StandardButton
            variant="primary"
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
          >
            New
          </StandardButton>
        </div>
      </div>

      {/* Table - Responsive with horizontal scroll */}
      <div className="overflow-x-auto">
        <table className="w-full">
          {/* Table Header - Gray background with uppercase labels */}
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
            <tr>
              {config.columns.map(column => (
                <th
                  key={column.id}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  style={{ width: column.width }}
                >
                  {column.label}
                </th>
              ))}
              {config.actions && config.actions.length > 0 && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>

          {/* Table Body - Hover effects on rows */}
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {data.map((row: any, rowIndex: number) => (
              <tr
                key={row.id || rowIndex}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                {config.columns.map(column => (
                  <td
                    key={column.id}
                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100"
                  >
                    {row[column.field]}
                  </td>
                ))}
                {config.actions && config.actions.length > 0 && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center space-x-3">
                      {config.actions.map(action => (
                        <StandardButton
                          key={action.id}
                          variant="link"
                          size="sm"
                          onClick={() => {
                            console.log(`Action: ${typeof action.action === 'string' ? action.action : 'function'}`, row);
                            // Execute action if it's a function
                            if (typeof action.action === 'function') {
                              action.action(row);
                            } else if (action.action === 'viewAuditDetails' && onViewAuditDetails) {
                              // Handle viewAuditDetails action - navigate to audit detail page
                              const auditId = row.id || row.auditId;
                              if (auditId) {
                                onViewAuditDetails(auditId);
                              }
                            }
                          }}
                        >
                          {action.label}
                        </StandardButton>
                      ))}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Table Footer - Record count */}
      {data.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Showing <span className="font-medium">{data.length}</span> record{data.length !== 1 ? 's' : ''}
            </p>
            {/* Pagination controls will be added in future enhancements */}
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicTableSection;
